import * as Sentry from '@sentry/react';
import { notify } from '@bynder/design-system';
import ProxyServerConnectorEvents from './ProxyServerConnectorEvents';
import { ResponseModel } from './ResponseModel';
import sleep from 'packages/helpers/sleep';
import { type RequestOptions, type ProgressInfo } from 'packages/common/LoadingBar/LoadingBar.types';

const DOWNLOAD_KEY = Symbol('download');

const reqJson = async (req: Request | Response) => {
    if (!('json' in req)) {
        return;
    }

    try {
        return await req.clone().json();
    } catch (error) {}
};

const cleanUrlOfFingerprint = (url: string) => {
    const values = window.location.href.split('/');
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname.replace(new RegExp(values.filter(Boolean).join('|'), 'g'), '');
    parsedUrl.searchParams.forEach((value, key) => {
        if (values.includes(value)) {
            parsedUrl.searchParams.delete(key);
        }
    });

    return `${pathname}?${parsedUrl.searchParams}`;
};

class ResponseError extends Error {
    constructor(status: number, url: string) {
        super(`STATUS: ${status}, to ${url}`);
    }
}

export default class ProxyServerConnector {
    static isBlocked = false;

    static getToken = (): string => '';

    static tokenRefresher = (requestObject: Request): Promise<any> => Promise.resolve(true);

    static requestsWaitList = [];

    static flushWaitList = () => {
        while (ProxyServerConnector.requestsWaitList.length) {
            ProxyServerConnector.requestsWaitList.shift()();
        }
    };

    static abortControllersWeakMap = new WeakMap<Promise<ResponseModel<any>>, AbortController>();

    private static _gatewayPrefix: string | null = null;

    static get gatewayPrefix(): string {
        return ProxyServerConnector._gatewayPrefix ?? GATEWAY_PREFIX;
    }

    static setGatewayPrefix(prefix: string) {
        ProxyServerConnector._gatewayPrefix = prefix;
    }

    basePath = '';

    private _baseUrl: string | null = null;

    constructor(basePath: string, baseUrl?: string) {
        this.basePath = basePath;
        this._baseUrl = baseUrl ?? null;
    }

    get baseUrl(): string {
        return ProxyServerConnector.gatewayPrefix ?? this._baseUrl;
    }

    static waitUntilUnblock = async (): Promise<boolean> => {
        if (ProxyServerConnector.isBlocked) {
            await sleep(200);
            await ProxyServerConnector.waitUntilUnblock();
        }

        return true;
    };

    static async handleResponseStatus(request: Request, response: Response, isStrict?: boolean): Promise<Response> {
        if (response.status >= 400) {
            const headers = {};
            (request.headers || []).forEach((value, key) => {
                headers[key] = value;
            });
            Sentry.captureException(new ResponseError(response.status, request.url), {
                fingerprint: ['{{ default }}', response.status.toString(), cleanUrlOfFingerprint(request.url)],
                extra: {
                    request: {
                        url: request.url,
                        method: request.method,
                        headers,
                        body: await reqJson(request),
                    },
                    response: {
                        status: response.status,
                        body: await reqJson(response),
                    },
                },
                level: 'log',
            });
            await Sentry.getClient()?.flush();
        }

        switch (response.status) {
            case 200: {
                break;
            }
            case 401: {
                window.dispatchEvent(ProxyServerConnectorEvents.onLogout);
                break;
            }
            case 403: {
                if (isStrict) {
                    window.dispatchEvent(ProxyServerConnectorEvents.onForbidden);
                } else {
                    notify({
                        title: 'Something went wrong',
                        variant: 'error',
                    });
                }

                break;
            }
            case 404: {
                if (isStrict) {
                    window.dispatchEvent(ProxyServerConnectorEvents.onNotFound);
                } else {
                    notify({
                        title: 'Something went wrong',
                        variant: 'error',
                    });
                }

                break;
            }
            case 498: {
                window.dispatchEvent(ProxyServerConnectorEvents.onRefreshTokenRequired);
                throw new Error('REFRESH_TOKEN_NEEDED');
            }
            case 500: {
                if (isStrict) {
                    window.dispatchEvent(ProxyServerConnectorEvents.onInternalError);
                } else {
                }

                break;
            }
        }

        return response;
    }

    static async handleResponseRawBody(response: Response): Promise<ResponseModel> {
        try {
            let jsonBody = null;

            if (response.headers.get('content-type')?.includes('application/json')) {
                jsonBody = await response.json();
            }

            return {
                json: jsonBody,
                status: response.status,
                headers: Object.fromEntries(response.headers),
            };
        } catch (e) {
            throw new Error('FAILED_JSON_PARSE');
        }
    }

    static async handleResponseDownload(response: Response): Promise<Omit<ResponseModel, 'json'>> {
        try {
            const blob = await response.blob();
            const fileName = ((response.headers.get('content-disposition') || '').split('filename=')[1] || '').slice(
                0,
                -1,
            );

            const a = document.createElement('a');
            a.href = window.URL.createObjectURL(blob);
            a.download = fileName;
            a.click();

            return {
                status: response.status,
                headers: Object.fromEntries(response.headers),
            };
        } catch (e) {
            throw new Error('FAILED_BLOB_PARSE');
        }
    }

    static handleResponseBody(response: ResponseModel): ResponseModel {
        return response;
    }

    static async trackResponseProgress(
        response: Response,
        onProgress: (info: ProgressInfo) => void,
    ): Promise<Response> {
        if (!response.body) {
            return response;
        }

        const contentLength = response.headers.get('Content-Length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;

        if (total <= 0) {
            return response;
        }

        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];
        let loaded = 0;

        const readableAsIterable = {
            [Symbol.asyncIterator]() {
                return {
                    async next(): Promise<IteratorResult<Uint8Array>> {
                        const { done, value } = await reader.read();

                        if (done) {
                            return { value: undefined as unknown as Uint8Array, done: true };
                        }

                        return { value, done: false };
                    },
                };
            },
        };

        for await (const value of readableAsIterable) {
            loaded += value.length;
            chunks.push(value);
            onProgress({
                loaded,
                total,
                progress: Math.min(100, (loaded / total) * 100),
            });
        }

        const blob = new Blob(chunks as BlobPart[]);

        return new Response(blob, {
            status: response.status,
            headers: response.headers,
            statusText: response.statusText,
        });
    }

    get = <T>(
        route: string,
        data?: Record<string, any>,
        headers?: Record<string, any>,
        isStrict?: boolean,
        options?: RequestOptions,
    ): Promise<ResponseModel<T>> => this.send(route, 'GET', {}, headers, isStrict, options);

    put = <T>(
        route: string,
        data?: Record<string, any>,
        headers?: Record<string, any>,
        isStrict?: boolean,
        options?: RequestOptions,
    ): Promise<ResponseModel<T>> => this.send(route, 'PUT', data, headers, isStrict, options);

    post = <T>(
        route: string,
        data: Record<string, any>,
        headers?: Record<string, any>,
        isStrict?: boolean,
        options?: RequestOptions,
    ): Promise<ResponseModel<T>> => this.send(route, 'POST', data, headers, isStrict, options);

    delete = (
        route: string,
        data?: Record<string, any>,
        headers?: Record<string, any>,
        isStrict?: boolean,
        options?: RequestOptions,
    ): Promise<ResponseModel> => this.send(route, 'DELETE', data, headers, isStrict, options);

    download = (route: string, data = {}, headers = {}): Promise<ResponseModel> =>
        this.send(
            route,
            'GET',
            {},
            {
                ...headers,
                [DOWNLOAD_KEY]: true,
            },
        );

    send = <T>(
        url: string,
        requestMethod: string,
        data: Record<string, any> = {},
        headers: Record<string, any> = {},
        isStrict = true,
        options?: RequestOptions,
    ): Promise<ResponseModel<T>> => {
        const abortController = new AbortController();
        const res = this._send<T>(url, requestMethod, data, headers, isStrict, abortController.signal, options);

        ProxyServerConnector.abortControllersWeakMap.set(res, abortController);

        return res;
    };

    private _send = async <T>(
        url: string,
        requestMethod: string,
        data: Record<string, unknown>,
        headers: Record<string | symbol, string>,
        isStrict: boolean,
        signal: AbortSignal,
        options?: RequestOptions,
    ): Promise<ResponseModel<T>> => {
        await ProxyServerConnector.waitUntilUnblock();

        const requestFullUrl: string = this.baseUrl + this.basePath + url;
        const isFormData = data instanceof FormData;

        // create body
        let requestBody: FormData | string | null = null;

        if (['POST', 'PUT', 'DELETE'].includes(requestMethod)) {
            requestBody = isFormData ? data : JSON.stringify(data);
        }
        // end: create body

        // create headers
        const requestHeaders: Headers = new Headers();
        const token: string = ProxyServerConnector.getToken();

        if (token) {
            requestHeaders.append('Authorization', 'Bearer ' + token);
        }

        // if (ProxyServerConnector.csrfToken) {
        //     requestHeaders.append('X-CSRF-Token', ProxyServerConnector.csrfToken);
        // }

        if (!isFormData) {
            requestHeaders.append('Content-Type', 'application/json');
        }

        requestHeaders.append('Cache-Control', 'no-cache');
        requestHeaders.append('Pragma', 'no-cache');

        const download = !!headers[DOWNLOAD_KEY];

        if (download) {
            delete headers[DOWNLOAD_KEY];
        }

        for (const headerKey in headers) {
            requestHeaders.append(headerKey, headers[headerKey]);
        }
        // end: create headers

        const requestObject = new Request(requestFullUrl, {
            method: requestMethod,
            credentials: 'same-origin',
            body: requestBody,
            headers: requestHeaders,
            signal,
        });

        return ProxyServerConnector.sendRequestObject<T>(requestObject, download, isStrict, options);
    };

    static sendRequestObject<T>(
        requestObject: Request,
        download?: boolean,
        isStrict?: boolean,
        options?: RequestOptions,
    ): Promise<ResponseModel<T>> {
        window.dispatchEvent(ProxyServerConnectorEvents.onBeforeRequest);

        const makeRequest = () => {
            let req = window
                .fetch(requestObject.clone())
                .then((res) => ProxyServerConnector.handleResponseStatus(requestObject, res, isStrict));

            if (options?.onProgress) {
                const { onProgress } = options;
                req = req.then((res) => ProxyServerConnector.trackResponseProgress(res, onProgress));
            }

            return download
                ? req.then(ProxyServerConnector.handleResponseDownload)
                : req.then(ProxyServerConnector.handleResponseRawBody).then(ProxyServerConnector.handleResponseBody);
        };

        return makeRequest()
            .catch((error) => {
                if (error.name === 'AbortError') {
                    // do nothing
                } else if (error?.message === 'FAILED_JSON_PARSE') {
                    window.dispatchEvent(ProxyServerConnectorEvents.onFailedToParseJson);
                } else if (error?.message === 'REFRESH_TOKEN_NEEDED') {
                    return ProxyServerConnector.tokenRefresher(requestObject);
                } else if (isNaN(error)) {
                    window.dispatchEvent(ProxyServerConnectorEvents.onFailedToFetch);
                }

                throw error;
            })
            .finally(() => {
                window.dispatchEvent(ProxyServerConnectorEvents.onAfterRequest);
            });
    }

    abortRequest(request: Promise<ResponseModel>) {
        const abortController = ProxyServerConnector.abortControllersWeakMap.get(request);

        if (abortController) {
            abortController.abort();
        }
    }
}
